<template>
  <section class="page-stack compare-page">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Character Lab</p>
          <h2>Character compare</h2>
          <p>Side-by-side score, item level, gear, rankings and raid progression for your shortlist.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Characters: <span>{{ compareList.length }}</span></span>
        <span class="filter-pill">Raid summaries: <span>{{ comparedRaids.length }}</span></span>
        <span class="filter-pill">Gear slots: <span>{{ gearSlots.length }}</span></span>
      </div>
      <div class="page-actions">
        <button class="btn-outline" type="button" :disabled="!compareList.length" @click="clearCompare">Clear list</button>
      </div>
    </div>

    <AppEmptyState
      v-if="!compareList.length"
      eyebrow="Compare"
      icon="fa-solid fa-scale-balanced"
      title="Nothing to compare yet"
      message="Add characters from Favorites or Mythic+ runs to build a side-by-side view of gear, score and progression."
      :actions="[
        { to: '/mythic-plus', label: 'Open Mythic+', icon: 'fa-solid fa-dungeon' },
        { to: '/favorites', label: 'Open Favorites', icon: 'fa-solid fa-star', variant: 'secondary' }
      ]"
    />

    <div v-else class="compare-stack">
      <div class="card compare-card">
        <div class="compare-sticky-head">
          <div
            class="compare-sticky-track"
            :style="{ ...compareGridStyle, transform: `translateX(-${stickyScroll.summary}px)` }"
          >
            <div class="compare-sticky-cell compare-sticky-label">Metric</div>
            <div v-for="entry in compareList" :key="`${entry.id}-summary-sticky`" class="compare-sticky-cell">
              <div class="compare-head">
                <div class="character-line">
                  <img
                    v-if="entry.profile.thumbnail_url"
                    class="avatar-image"
                    :src="entry.profile.thumbnail_url"
                    :alt="entry.profile.name"
                    loading="lazy"
                  >
                  <div>
                    <strong>{{ entry.profile.name }}</strong>
                    <p>{{ entry.profile.class }} / {{ entry.profile.active_spec_name }}</p>
                  </div>
                </div>
                <button class="btn-icon-outline compare-remove-button" type="button" :aria-label="`Remove ${entry.profile.name} from compare`" title="Remove from compare" @click="removeFromCompare(entry.id)">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <section class="compare-table-wrap" @scroll="syncStickyHeader('summary', $event)">
          <table class="table compare-table" :style="compareTableStyle">
            <colgroup>
              <col class="compare-label-col">
              <col v-for="entry in compareList" :key="`${entry.id}-summary-col`" class="compare-character-col">
            </colgroup>
            <tbody>
              <tr>
                <td>Realm / Region</td>
                <td v-for="entry in compareList" :key="`${entry.id}-realm`">{{ entry.profile.realm }} / {{ entry.profile.region.toUpperCase() }}</td>
              </tr>
              <tr>
                <td>Item level</td>
                <td v-for="entry in compareList" :key="`${entry.id}-ilvl`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'itemLevel') }">
                  {{ itemLevel(entry.profile) }}
                </td>
              </tr>
              <tr>
                <td>Equipped slots</td>
                <td v-for="entry in compareList" :key="`${entry.id}-slots`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'equippedSlots') }">
                  {{ equippedSlotsLabel(entry.profile) }}
                </td>
              </tr>
              <tr>
                <td>Weapon item level</td>
                <td v-for="entry in compareList" :key="`${entry.id}-weapon-ilvl`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'weaponLevel') }">
                  {{ gearGroupLevel(entry.profile, weaponSlots) }}
                </td>
              </tr>
              <tr>
                <td>Trinket item level</td>
                <td v-for="entry in compareList" :key="`${entry.id}-trinket-ilvl`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'trinketLevel') }">
                  {{ gearGroupLevel(entry.profile, trinketSlots) }}
                </td>
              </tr>
              <tr>
                <td>Achievement points</td>
                <td v-for="entry in compareList" :key="`${entry.id}-ap`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'achievementPoints') }">
                  {{ entry.profile.achievement_points || '-' }}
                </td>
              </tr>
              <tr>
                <td>M+ score</td>
                <td v-for="entry in compareList" :key="`${entry.id}-score`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'mythicScore') }">
                  {{ bestScore(entry.profile) }}
                </td>
              </tr>
              <tr>
                <td>World rank</td>
                <td v-for="entry in compareList" :key="`${entry.id}-rank-world`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'worldRank') }">
                  {{ entry.profile.mythic_plus_ranks?.overall?.world || '-' }}
                </td>
              </tr>
              <tr>
                <td>Region rank</td>
                <td v-for="entry in compareList" :key="`${entry.id}-rank-region`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'regionRank') }">
                  {{ entry.profile.mythic_plus_ranks?.overall?.region || '-' }}
                </td>
              </tr>
              <tr>
                <td>Realm rank</td>
                <td v-for="entry in compareList" :key="`${entry.id}-rank-realm`" :class="{ 'is-best-metric': isBestMetric(entry.profile, 'realmRank') }">
                  {{ entry.profile.mythic_plus_ranks?.overall?.realm || '-' }}
                </td>
              </tr>
              <tr v-for="raid in comparedRaids" :key="raid.slug">
                <td>{{ raid.slug }}</td>
                <td v-for="entry in compareList" :key="`${entry.id}-${raid.slug}`">{{ raid.summaryById[entry.id] || '-' }}</td>
              </tr>
              <tr>
                <td>Best run</td>
                <td v-for="entry in compareList" :key="`${entry.id}-best-run`">{{ bestRunLabel(entry.profile) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      <div class="card compare-card">
        <header class="section-head">
          <div>
            <p class="eyebrow">Equipment Matrix</p>
            <h3>Gear by slot</h3>
          </div>
          <p>Best item level in each slot is highlighted.</p>
        </header>

        <div class="compare-sticky-head">
          <div
            class="compare-sticky-track"
            :style="{ ...compareGridStyle, transform: `translateX(-${stickyScroll.gear}px)` }"
          >
            <div class="compare-sticky-cell compare-sticky-label">Slot</div>
            <div v-for="entry in compareList" :key="`${entry.id}-gear-sticky`" class="compare-sticky-cell">
              <div class="compare-head">
                <div class="character-line">
                  <img
                    v-if="entry.profile.thumbnail_url"
                    class="avatar-image"
                    :src="entry.profile.thumbnail_url"
                    :alt="entry.profile.name"
                    loading="lazy"
                  >
                  <div>
                    <strong>{{ entry.profile.name }}</strong>
                    <p>{{ entry.profile.class }} / {{ entry.profile.active_spec_name }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section class="compare-table-wrap" @scroll="syncStickyHeader('gear', $event)">
          <table class="table compare-table gear-compare-table" :style="compareTableStyle">
            <colgroup>
              <col class="compare-label-col">
              <col v-for="entry in compareList" :key="`${entry.id}-gear-col`" class="compare-character-col">
            </colgroup>
            <tbody>
              <tr v-for="slot in gearSlotRows" :key="slot.key">
                <td>
                  <strong>{{ slot.label }}</strong>
                </td>
                <td v-for="entry in compareList" :key="`${entry.id}-${slot.key}-gear`" :class="{ 'is-best-gear': isBestSlotItemLevel(entry.profile, slot.key) }">
                  <a
                    v-if="gearItemBySlot(entry.profile, slot.key)"
                    class="compare-gear-item"
                    :href="wowheadItemUrl(gearItemBySlot(entry.profile, slot.key))"
                    :data-wowhead="wowheadItemData(gearItemBySlot(entry.profile, slot.key))"
                    target="_blank"
                    rel="noreferrer"
                    :title="gearItemName(entry.profile, slot.key)"
                  >
                    <ItemIcon
                      :item="gearItemBySlot(entry.profile, slot.key)"
                      :alt="gearItemName(entry.profile, slot.key)"
                      loading="eager"
                    />
                    <span>
                      <strong>{{ gearItemName(entry.profile, slot.key) }}</strong>
                      <small>ilvl {{ gearItemLevel(entry.profile, slot.key) }}</small>
                    </span>
                  </a>
                  <span v-else class="compare-gear-empty">Not equipped</span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import ItemIcon from '../components/ItemIcon.vue'
import { getBestMythicScore, summarizeRaidProgress } from '../utils/formatters'
import { ensureWowheadTooltips, refreshWowheadTooltips } from '../utils/wowheadTooltips'

const LABEL_COLUMN_REM = 10
const CHARACTER_COLUMN_REM = 17

export default {
  name: 'CompareView',
  components: {
    AppEmptyState,
    ItemIcon,
  },
  data() {
    return {
      stickyScroll: {
        summary: 0,
        gear: 0,
      },
      gearSlots: [
        'head',
        'neck',
        'shoulder',
        'back',
        'chest',
        'shirt',
        'tabard',
        'wrist',
        'hands',
        'waist',
        'legs',
        'feet',
        'finger1',
        'finger2',
        'trinket1',
        'trinket2',
        'mainhand',
        'offhand',
      ],
      weaponSlots: ['mainhand', 'offhand'],
      trinketSlots: ['trinket1', 'trinket2'],
    }
  },
  computed: {
    compareList() {
      return this.$store.getters['getCompareList']
    },
    comparedRaids() {
      const raids = {}

      this.compareList.forEach((entry) => {
        summarizeRaidProgress(entry.profile).forEach((raid) => {
          if (!raids[raid.slug]) {
            raids[raid.slug] = { slug: raid.slug, summaryById: {} }
          }
          raids[raid.slug].summaryById[entry.id] = raid.summary
        })
      })

      return Object.values(raids)
    },
    gearSlotRows() {
      return this.gearSlots.map((slot) => ({
        key: slot,
        label: this.slotLabel(slot),
      }))
    },
    compareTableStyle() {
      return {
        minWidth: `${this.compareTableWidthRem}rem`,
        width: '100%',
      }
    },
    compareGridStyle() {
      return {
        display: 'grid',
        gridTemplateColumns: `${LABEL_COLUMN_REM}rem repeat(${this.compareList.length}, minmax(${CHARACTER_COLUMN_REM}rem, 1fr))`,
        minWidth: `${this.compareTableWidthRem}rem`,
        width: '100%',
      }
    },
    compareTableWidthRem() {
      return LABEL_COLUMN_REM + (this.compareList.length * CHARACTER_COLUMN_REM)
    },
  },
  watch: {
    compareList() {
      this.refreshWowheadTooltips()
    },
  },
  mounted() {
    this.ensureWowheadTooltips()
  },
  methods: {
    removeFromCompare(id) {
      const entry = this.compareList.find((item) => item.id === id)
      this.$store.dispatch('requestConfirmation', {
        title: 'Remove from compare?',
        message: `Remove ${entry?.profile?.name || 'this character'} from the compare table?`,
        confirmLabel: 'Remove',
        action: 'removeFromCompare',
        payload: id,
      })
    },
    clearCompare() {
      this.$store.dispatch('requestConfirmation', {
        title: 'Clear compare list?',
        message: `Remove all ${this.compareList.length} characters from compare?`,
        confirmLabel: 'Clear list',
        action: 'clearCompare',
      })
    },
    syncStickyHeader(key, event) {
      this.stickyScroll[key] = event.target.scrollLeft
    },
    bestScore(profile) {
      return getBestMythicScore(profile).toFixed(1)
    },
    bestRunLabel(profile) {
      const bestRun = profile.mythic_plus_best_runs?.[0]
      return bestRun ? `${bestRun.dungeon} +${bestRun.mythic_level}` : '-'
    },
    itemLevel(profile) {
      const level = profile.gear?.item_level_equipped
      return level ? Math.round(Number(level)).toString() : '-'
    },
    numericItemLevel(profile) {
      return this.numberOrZero(profile.gear?.item_level_equipped)
    },
    equippedSlotsCount(profile) {
      return this.normalizedGearItems(profile).filter((item) => item?.name || item?.item_id || item?.id).length
    },
    equippedSlotsLabel(profile) {
      return `${this.equippedSlotsCount(profile)}/${this.gearSlots.length}`
    },
    normalizedGearItems(profile) {
      const items = profile.gear?.items
      if (!items) return []

      if (Array.isArray(items)) {
        return items.map((item, index) => this.normalizeGearItem(item, item?.slot || item?.slot_key || `${index}`))
      }

      return Object.entries(items).map(([slotKey, item]) => this.normalizeGearItem(item, slotKey))
    },
    normalizeGearItem(item, slotKey) {
      return {
        ...(item || {}),
        slotKey: this.normalizeSlotKey(slotKey || item?.slot || item?.inventory_type?.type),
      }
    },
    normalizeSlotKey(slot) {
      return String(slot || '')
        .toLowerCase()
        .replace(/_/g, '')
        .replace(/-/g, '')
        .replace(/\s/g, '')
    },
    gearItemBySlot(profile, slot) {
      const normalizedSlot = this.normalizeSlotKey(slot)
      return this.normalizedGearItems(profile).find((item) => item.slotKey === normalizedSlot)
    },
    gearItemName(profile, slot) {
      return this.gearItemBySlot(profile, slot)?.name || this.slotLabel(slot)
    },
    gearItemLevel(profile, slot) {
      const item = this.gearItemBySlot(profile, slot)
      return item?.item_level || item?.itemLevel || '-'
    },
    numericGearItemLevel(profile, slot) {
      const level = Number(this.gearItemLevel(profile, slot))
      return Number.isFinite(level) ? level : 0
    },
    bestSlotItemLevel(slot) {
      return Math.max(...this.compareList.map((entry) => this.numericGearItemLevel(entry.profile, slot)), 0)
    },
    isBestSlotItemLevel(profile, slot) {
      const level = this.numericGearItemLevel(profile, slot)
      return level > 0 && level === this.bestSlotItemLevel(slot)
    },
    gearGroupLevel(profile, slots) {
      const average = this.numericGearGroupLevel(profile, slots)
      return average ? Math.round(average).toString() : '-'
    },
    numericGearGroupLevel(profile, slots) {
      const levels = slots
        .map((slot) => this.numericGearItemLevel(profile, slot))
        .filter((level) => level > 0)
      if (!levels.length) return 0

      return levels.reduce((sum, level) => sum + level, 0) / levels.length
    },
    metricValue(profile, metric) {
      const values = {
        itemLevel: () => this.numericItemLevel(profile),
        equippedSlots: () => this.equippedSlotsCount(profile),
        weaponLevel: () => this.numericGearGroupLevel(profile, this.weaponSlots),
        trinketLevel: () => this.numericGearGroupLevel(profile, this.trinketSlots),
        achievementPoints: () => this.numberOrZero(profile.achievement_points),
        mythicScore: () => getBestMythicScore(profile),
        worldRank: () => this.numberOrZero(profile.mythic_plus_ranks?.overall?.world),
        regionRank: () => this.numberOrZero(profile.mythic_plus_ranks?.overall?.region),
        realmRank: () => this.numberOrZero(profile.mythic_plus_ranks?.overall?.realm),
      }
      return values[metric]?.() || 0
    },
    bestMetricValue(metric) {
      const values = this.compareList
        .map((entry) => this.metricValue(entry.profile, metric))
        .filter((value) => value > 0)
      if (!values.length) return 0

      return this.isLowerBetterMetric(metric) ? Math.min(...values) : Math.max(...values)
    },
    isBestMetric(profile, metric) {
      const value = this.metricValue(profile, metric)
      return value > 0 && value === this.bestMetricValue(metric)
    },
    isLowerBetterMetric(metric) {
      return ['worldRank', 'regionRank', 'realmRank'].includes(metric)
    },
    numberOrZero(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    slotLabel(slot) {
      const labels = {
        head: 'Head',
        neck: 'Neck',
        shoulder: 'Shoulder',
        back: 'Back',
        chest: 'Chest',
        shirt: 'Shirt',
        tabard: 'Tabard',
        wrist: 'Wrist',
        hands: 'Hands',
        waist: 'Waist',
        legs: 'Legs',
        feet: 'Feet',
        finger1: 'Finger 1',
        finger2: 'Finger 2',
        trinket1: 'Trinket 1',
        trinket2: 'Trinket 2',
        mainhand: 'Main hand',
        offhand: 'Off hand',
      }
      return labels[slot] || 'Empty slot'
    },
    wowheadItemUrl(item) {
      const itemId = item?.item_id || item?.id
      return itemId ? `https://www.wowhead.com/item=${itemId}` : 'https://www.wowhead.com/'
    },
    wowheadItemData(item) {
      const itemId = item?.item_id || item?.id
      if (!itemId) return null

      const params = [`item=${itemId}`]
      const itemLevel = item.item_level || item.itemLevel
      if (itemLevel) params.push(`ilvl=${itemLevel}`)
      if (Array.isArray(item.bonuses) && item.bonuses.length) params.push(`bonus=${item.bonuses.join(':')}`)
      if (Array.isArray(item.gems) && item.gems.length) params.push(`gems=${item.gems.map((gem) => gem.item_id || gem.id || gem).join(':')}`)
      if (item.enchant?.id || item.enchant_id) params.push(`ench=${item.enchant?.id || item.enchant_id}`)
      return params.join('&')
    },
    ensureWowheadTooltips() {
      ensureWowheadTooltips()
    },
    refreshWowheadTooltips() {
      this.$nextTick(() => {
        refreshWowheadTooltips()
      })
    },
  },
}
</script>
